import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12 bg-background">
      <div className="w-full max-w-sm" data-testid="reset-password-page">
        <img src="/logo.png" alt="Caring Hands Concierge Nursing" className="h-20 w-20 object-contain mb-6" />
        <div className="label-caps mb-2">Reset password</div>
        <h2 className="font-display font-bold text-3xl tracking-tighter mb-8">Set a new password</h2>

        {!token ? (
          <div className="text-sm font-mono border border-destructive/40 bg-destructive/5 px-3 py-3" data-testid="reset-missing-token">
            This reset link is missing its token. Please request a new link from the
            "Forgot password" page.
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-password-form">
            <div className="mb-4">
              <Label className="label-caps">New password</Label>
              <Input
                type="password"
                className="rounded-none mt-2 h-11"
                value={form.password}
                onChange={upd("password")}
                required
                data-testid="reset-password-input"
              />
            </div>
            <div className="mb-4">
              <Label className="label-caps">Confirm password</Label>
              <Input
                type="password"
                className="rounded-none mt-2 h-11"
                value={form.confirm}
                onChange={upd("confirm")}
                required
                data-testid="reset-confirm-input"
              />
            </div>

            {error && (
              <div className="mb-4 text-destructive text-sm font-mono border border-destructive/40 bg-destructive/5 px-3 py-2" data-testid="reset-error">
                {error}
              </div>
            )}

            <Button type="submit" disabled={busy} className="w-full rounded-none h-11 mt-2" data-testid="reset-submit-button">
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
